import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Input, Popconfirm, Space, Table, Tag, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CopyOutlined, DeleteOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { del, get } from '../../api/request';
import { API_ENDPOINTS, buildApiUrl } from '../../api/config';
import './ResourceListPage.css';

interface StorageProvider {
  id: number;
  name: string;
  provider_type: string;
  bucket: string;
  is_default: boolean;
}

interface ResourceItem {
  object_key: string;
  name: string;
  prefix: string;
  size: number;
  last_modified?: string;
  etag: string;
  storage_class: string;
  url: string;
}

interface ResourceListResponse {
  provider: StorageProvider;
  bucket: string;
  prefix: string;
  items: ResourceItem[];
  total: number;
}

const formatSize = (value: number) => {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / 1024 / 1024).toFixed(1)} MB`;
  return `${(value / 1024 / 1024 / 1024).toFixed(1)} GB`;
};

const ResourceListPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [prefixInput, setPrefixInput] = useState('');
  const [prefix, setPrefix] = useState('');
  const [data, setData] = useState<ResourceListResponse | null>(null);

  const fetchResources = async (nextPrefix = prefix) => {
    setLoading(true);
    try {
      const endpoint = buildApiUrl(API_ENDPOINTS.storage.resources, {
        prefix: nextPrefix,
        limit: 500,
      });
      const result = await get<ResourceListResponse>(endpoint);
      setData(result);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载资源列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResources('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const providerLabel = useMemo(() => {
    if (!data?.provider) return '默认云存储';
    return `${data.provider.name} / ${data.bucket}`;
  }, [data]);

  const applyPrefix = () => {
    const value = prefixInput.trim();
    setPrefix(value);
    fetchResources(value);
  };

  const copyUrl = async (record: ResourceItem) => {
    try {
      await navigator.clipboard.writeText(record.url);
      message.success('资源地址已复制');
    } catch {
      message.warning('复制失败，请手动复制地址');
    }
  };

  const deleteResource = async (record: ResourceItem) => {
    try {
      const endpoint = buildApiUrl(API_ENDPOINTS.storage.resources, { object_key: record.object_key });
      await del(endpoint);
      message.success('资源已删除');
      fetchResources(prefix);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '删除资源失败');
    }
  };

  const columns: ColumnsType<ResourceItem> = [
    {
      title: '资源',
      dataIndex: 'object_key',
      render: (value, record) => (
        <Tooltip title={value}>
          <div className="resource-key">
            <strong>{record.name}</strong>
            {record.prefix && <div>{record.prefix}</div>}
          </div>
        </Tooltip>
      ),
    },
    {
      title: '大小',
      dataIndex: 'size',
      width: 110,
      render: (value) => formatSize(value),
    },
    {
      title: '更新时间',
      dataIndex: 'last_modified',
      width: 190,
      render: (value) => (value ? new Date(value).toLocaleString() : '-'),
    },
    {
      title: '存储类型',
      dataIndex: 'storage_class',
      width: 120,
      render: (value) => <Tag>{value || 'STANDARD'}</Tag>,
    },
    {
      title: '操作',
      width: 170,
      render: (_, record) => (
        <div className="resource-actions">
          <Button size="small" icon={<CopyOutlined />} onClick={() => copyUrl(record)}>
            地址
          </Button>
          <Popconfirm title="确认删除这个资源？" onConfirm={() => deleteResource(record)}>
            <Button size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="resource-list-page">
      <h1 className="page-title">资源列表</h1>
      <Alert
        className="resource-summary"
        type="info"
        showIcon
        message="这里始终展示当前系统存储中的资源。平台文件服采用内容寻址且不提供物理删除接口，删除操作按其协议幂等成功。"
      />

      <Card bordered={false}>
        <div className="resource-toolbar">
          <Space wrap>
            <Tag color="blue">{providerLabel}</Tag>
            <Tag color="purple">对象数: {data?.total ?? 0}</Tag>
          </Space>
          <Space.Compact className="resource-prefix-search">
            <Input
              allowClear
              value={prefixInput}
              onChange={(event) => setPrefixInput(event.target.value)}
              onPressEnter={applyPrefix}
              placeholder="按前缀筛选，例如 users/1/projects/"
            />
            <Button icon={<SearchOutlined />} onClick={applyPrefix}>筛选</Button>
            <Button icon={<ReloadOutlined />} onClick={() => fetchResources(prefix)}>刷新</Button>
          </Space.Compact>
        </div>

        <Table
          rowKey="object_key"
          columns={columns}
          dataSource={data?.items || []}
          loading={loading}
          pagination={{ pageSize: 12 }}
          scroll={{ x: 860 }}
          locale={{ emptyText: '当前 bucket 暂无资源' }}
        />
      </Card>
    </div>
  );
};

export default ResourceListPage;
